import {
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeRadiusVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library.base-ui.theme";
import { Box } from "@caspeco/casper-ui-library.components.box";
import { Flex } from "@caspeco/casper-ui-library.components.flex";
import { Heading } from "@caspeco/casper-ui-library.components.heading";
import { Text } from "@caspeco/casper-ui-library.components.text";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import BackButton from "components/stepper/back-button";
import { getStepperProgressPercentage } from "components/stepper/stepper-helper";
import StepperSkeleton from "components/stepper/stepper-skeleton";
import { ActiveStep, useActiveStep } from "components/stepper/use-active-step";
import { useBookingNavigate } from "components/stepper/use-booking-navigate";
import { useBookingStateContext } from "hooks/use-booking-state";
import { usePaymentRule } from "hooks/use-payment-rule";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Fragment } from "react/jsx-runtime";
import { Routes } from "routes";
import { PaymentRule } from "types/payment";

export const Stepper = () => {
    const externalBookingSettings = useExternalBookingSettings();
    const state = useBookingStateContext();
    const { t } = useTranslation();
    const location = useLocation();
    const activeStep = useActiveStep();
    const paymentRule = usePaymentRule();
    const { bookingNavigate } = useBookingNavigate();

    if (externalBookingSettings.isLoading) return <StepperSkeleton />;

    const unitMetaData = externalBookingSettings.data?.unitsMetaData;
    const { maxWebGuests, activateWebChildren } = unitMetaData ?? {};

    const { guests, guestsChildren, date, isActivityBooking } = state;

    const dateText = date?.locale(i18n.language).format("D MMM YYYY");
    let guestText = t("start.numberGuests", { guests });

    if (guestsChildren) {
        const childrenText = t("start.ofWhich", { children: guestsChildren });
        guestText = `${guestText} ${childrenText}`;
    }

    const isPrepayment = paymentRule === PaymentRule.Prepayment;
    const stepContext = [
        {
            condition: ActiveStep.Guests,
            summaryText: [t("stepper.welcome")],
            stepTitle: t("stepper.chooseGuestSize"),
            hideBackButton: true,
            navigateBack: () => {},
        },
        {
            condition: ActiveStep.GuestsChildren,
            summaryText: [guestText],
            stepTitle: t("stepper.chooseGuestChildren"),
            navigateBack: () => bookingNavigate({ fromRoute: Routes.WIZARD, back: true }),
        },
        {
            condition: ActiveStep.Date,
            summaryText: [guestText],
            stepTitle: t("stepper.chooseDate"),
            navigateBack: () => bookingNavigate({ fromRoute: Routes.WIZARD, back: true }),
        },
        {
            condition: ActiveStep.Times,
            summaryText: [guestText, dateText],
            stepTitle: isActivityBooking ? t("stepper.chooseActivities") : t("stepper.chooseTime"),
            navigateBack: () => bookingNavigate({ fromRoute: Routes.AVAILABILITY, back: true }),
        },
        {
            condition: ActiveStep.Preorder,
            summaryText: [guestText, dateText],
            stepTitle: t("stepper.preorder"),
            navigateBack: () => bookingNavigate({ fromRoute: Routes.PREORDER, back: true }),
        },
        {
            condition: ActiveStep.Finalize,
            summaryText: [guestText, dateText],
            stepTitle: isPrepayment
                ? t("stepper.confirmDetailsAndPayment")
                : t("stepper.confirmDetails"),
            navigateBack: () => bookingNavigate({ fromRoute: Routes.FINALIZE, back: true }),
        },
    ];

    const progressbarHeight = "0.25rem";
    const progress = getStepperProgressPercentage(
        state,
        activateWebChildren ?? false,
        maxWebGuests ?? 0,
        location.pathname
    );
    const stepTitle = stepContext.find((step) => step.condition === activeStep)?.stepTitle || "";
    return (
        <Box
            role="navigation"
            aria-label={t("stepper.ariaLabelWithStep", { stepTitle })}
            display="flex"
            flexDirection="column"
            pb={ThemeSpaceVariable.Large}
            pt={ThemeSpaceVariable.Medium}
            px={ThemeSpaceVariable.Medium}
        >
            <Flex
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
            >
                <Box
                    flex={1}
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    gap={ThemeSpaceVariable.XSmall}
                    fontSize={ThemeFontSizeVariable.Medium}
                >
                    {stepContext.map((step) => {
                        if (step.condition === activeStep) {
                            return (
                                <Flex
                                    key={step.condition}
                                    w="full"
                                    textAlign="center"
                                    align="center"
                                >
                                    {step.hideBackButton ? (
                                        <Flex flex={1} justifyContent="flex-start" />
                                    ) : (
                                        <Flex flex={1} align="center">
                                            <BackButton
                                                onClick={() => {
                                                    void step.navigateBack();
                                                }}
                                            />
                                        </Flex>
                                    )}

                                    <Flex direction="column" align="center" flex={3}>
                                        <Flex
                                            justify="center"
                                            direction="row"
                                            gap={ThemeSpaceVariable.XSmall}
                                            flexWrap="wrap"
                                            align="center"
                                            color={ThemeColorVariable.OnBackgroundSubdued}
                                        >
                                            {step.summaryText.map((text, index) => (
                                                <Fragment key={index}>
                                                    <Text
                                                        key={index}
                                                        as="span"
                                                        fontSize={ThemeFontSizeVariable.Small}
                                                        color={
                                                            ThemeColorVariable.OnBackgroundSubdued
                                                        }
                                                    >
                                                        {text}
                                                        {index !== step.summaryText.length - 1 &&
                                                            ","}
                                                    </Text>
                                                </Fragment>
                                            ))}
                                        </Flex>

                                        <Heading
                                            as="h2"
                                            fontWeight="bold"
                                            fontSize={ThemeFontSizeVariable.Medium}
                                            lineHeight={1.5}
                                        >
                                            {step.stepTitle}
                                        </Heading>
                                    </Flex>
                                    <Flex flex="1" />
                                </Flex>
                            );
                        }
                        return null;
                    })}
                </Box>
            </Flex>

            <Box
                position="relative"
                width="full"
                height={progressbarHeight}
                mt={ThemeSpaceVariable.Small}
                bg={ThemeColorVariable.BackgroundDisabled}
                borderRadius={ThemeRadiusVariable.XSmall}
                overflow="hidden"
                role="progressbar"
                aria-valuenow={progress}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={t("stepper.progress", { progress: `${progress}%` })}
            >
                <Box width={`${progress}%`} height="full" background={ThemeColorVariable.Primary} />
            </Box>
        </Box>
    );
};
